<template>
  <div class="max-w-3xl space-y-4">
    <!-- Page header -->
    <div class="flex items-center gap-3">
      <span class="flex items-center justify-center w-10 h-10 rounded-xl bg-taxiye-100 text-taxiye-500">
        <span class="material-icons-round">person_add</span>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800">New User</h2>
        <p class="text-sm text-gray-500">Add a new admin user and assign their roles.</p>
      </div>
    </div>

    <form @submit.prevent="add">
      <div class="p-6 space-y-6 bg-white border border-gray-200 rounded-2xl shadow-sm">
        <!-- Name row -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">First Name</span>
            <input
              v-model="firstName"
              class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Abbay"
            />
            <span class="block mt-1 text-xs text-red-500" v-if="firstNameError">{{ firstNameError }}</span>
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Last Name</span>
            <input
              v-model="lastName"
              class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              placeholder="Kassa"
            />
            <span class="block mt-1 text-xs text-red-500" v-if="lastNameError">{{ lastNameError }}</span>
          </label>
        </div>

        <!-- Email -->
        <label class="block">
          <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Email</span>
          <input
            v-model="email"
            class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
            type="email"
            placeholder="contact@sheraton.com"
          />
          <span class="block mt-1 text-xs text-red-500" v-if="emailError">{{ emailError }}</span>
        </label>

        <!-- Password row -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Password</span>
            <input
              v-model="password"
              class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              type="password"
              placeholder="Password"
            />
            <span class="block mt-1 text-xs text-red-500" v-if="passwordError">{{ passwordError }}</span>
          </label>
          <label class="block">
            <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Confirm Password</span>
            <input
              v-model="confirmPassword"
              class="w-full h-10 px-3 mt-1.5 text-sm text-gray-800 border-gray-300 shadow-sm form-input rounded-xl focus:border-taxiye-300"
              type="password"
              placeholder="Confirm password"
            />
            <span class="block mt-1 text-xs text-red-500" v-if="confirmPasswordError">{{ confirmPasswordError }}</span>
          </label>
        </div>

        <!-- Roles -->
        <div>
          <span class="block text-xs font-semibold tracking-wider text-gray-600 uppercase">Roles</span>
          <div v-if="loadingRoles" class="flex items-center mt-2 space-x-2 text-sm text-gray-500">
            <i class="text-base material-icons-round spin">sync</i>
            <span>Loading roles...</span>
          </div>
          <div class="flex flex-wrap gap-2 mt-2" v-else>
            <label
              v-for="role in roles"
              :key="role._id"
              :for="role.roleName"
              class="inline-flex items-center px-3 py-2 text-sm border cursor-pointer rounded-xl transition-colors duration-150"
              :class="roleSelection[role._id]
                ? 'border-taxiye-300 bg-taxiye-50 text-taxiye-700'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'"
            >
              <input
                type="checkbox"
                :name="role.roleName"
                v-model="roleSelection[role._id]"
                :id="role.roleName"
                class="mr-2 border-gray-300 rounded form-checkbox text-taxiye-500 focus:border-taxiye-300"
              />
              <span class="font-medium">{{ role.roleName }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-between mt-4">
        <back-button></back-button>
        <button
          :disabled="busy || !isValid"
          class="inline-flex items-center h-10 px-5 text-sm font-semibold text-white shadow-sm rounded-xl transition-colors duration-150"
          :class="
            (busy || !isValid)
              ? 'bg-taxiye-200 cursor-not-allowed'
              : 'bg-taxiye-500 hover:bg-taxiye-600'
          "
        >
          <i v-if="busy" class="mr-1 text-base material-icons-round spin">sync</i>
          <i v-else class="mr-1 text-base material-icons-round">add</i>
          <span>Add User</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import BackButton from "@/components/BackButton.vue";

export default {
  components: {
    BackButton,
  },
  data() {
    return {
      busy: false,
      firstName: null,
      lastName: null,
      email: null,
      password: null,
      confirmPassword: null,
      dataValidationError: {
        email: null
      },
      roles:[],
      roleSelection: {},
      loadingRoles: true,
    };
  },
  created() {
    this.fetchRoles();
  },
  computed: {
    firstNameError() {
      if (this.firstName === null) return null;

      if (this.firstName.length < 2) return "Must be > 2 in length";

      return null;
    },
    lastNameError() {
      if (this.lastName === null) return null;

      if (this.lastName.length < 2) return "Must be > 2 in length";

      return null;
    },
    emailError() {
      if (this.email === null) return null;

      if (this.email.length < 2) return "Must be > 2 in length";

      if (!/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(this.email)) {
        return "Invalid email address";
      }

      return null;
    },
    passwordError() {
      if (this.password === null) return null;

      if (this.password.length < 2) return "Must be > 2 in length";

      return null;
    },
    confirmPasswordError() {
      if (this.confirmPassword === null) return null;

      if (this.confirmPassword.length < 2) return "Must be > 2 in length";

      if (this.confirmPassword !== this.password) return "Passwords don't match.";

      return null;
    },
    isValid() {
      return [
        this.firstName,
        this.lastName,
        this.password,
        this.confirmPassword,
        this.email,
      ].every(x => x) && [
        this.firstNameError,
        this.lastNameError,
        this.emailError,
        this.passwordError,
        this.confirmPasswordError,
      ].every(x => x == null);
    }
  },
  methods: {
    add() {
      const roles = Object.entries(this.roleSelection).filter(([, v]) => v).map(([k]) => k)
      if (this.password != "" && this.password == this.confirmPassword) {
        var data = {
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
          roles
        };
        console.log(data);
          this.busy = true;
          this.$misc
            .post("/accounts", data)
            .then((response) => {
              console.log(response);
              window.flash({
                body: "User created successfully!",
                type: "success",
              });
              // this.$router.push("/accounts")
            })
            .catch(err => {
              window.flash({
                type: "error",
                body: err.response.data,
              });
            })
            .finally(() => (this.busy = false));
            
      }
    },

    fetchRoles() {
      this.loadingRoles = true;
      this.$misc
        .get("roles/")
        .then(({ data : { data } }) => {
          this.roles = data
          for (const role of data) {
            this.roleSelection[role._id] = false
          }
          this.loadingRoles = false;
        });

    },
  }
};
</script>
